import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaSave, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import toast from "react-hot-toast";
import {
  useGetModuleFieldsQuery,
  useSaveModuleFieldConfigMutation,
} from "../toolkitServices/moduleFieldsApi";

/* ---------------- Skeleton Loader ---------------- */
const SkeletonRow = () => (
  <div className="animate-pulse flex items-center justify-between px-4 py-3">
    <div>
      <div className="h-3 w-32 bg-slate-200 rounded mb-1" />
      <div className="h-2 w-24 bg-slate-100 rounded" />
    </div>
    <div className="h-7 w-20 bg-slate-200 rounded-full" />
  </div>
);

const ModuleFieldSettings = ({ module = "LABELLED_STOCK" }) => {
  const { data, isLoading, isError, refetch } = useGetModuleFieldsQuery(module);

  const [saveConfig, { isLoading: isSaving }] =
    useSaveModuleFieldConfigMutation();

  const [fields, setFields] = useState([]);

  /* ------------ load fields ------------ */
  useEffect(() => {
    if (data?.data) setFields(data.data);
  }, [data]);

  /* ------------ toggle ------------ */
  const toggleVisibility = (index) => {
    setFields((prev) =>
      prev.map((f, i) =>
        i === index ? { ...f, visibility: !f.visibility } : f
      )
    );
  };

  /* ------------ save ------------ */
  const handleSave = async () => {
    try {
      const payload = fields.map((f) => ({
        fieldName: f.name,
        visibility: f.visibility,
      }));

      await saveConfig({ module, fields: payload }).unwrap();
      toast.success("Field configuration saved");
      refetch();
    } catch (e) {
      toast.error(e?.data?.msg || "Failed to save configuration");
    }
  };

  /* ------------ group by type ------------ */
  const grouped = useMemo(() => {
    const map = {};
    fields.forEach((f) => {
      const type = f.type || "text";
      if (!map[type]) map[type] = [];
      map[type].push(f);
    });
    return map;
  }, [fields]);

  /* ------------ loading ------------ */
  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow border p-4 space-y-3">
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 text-sm text-rose-500">
        Failed to load module fields
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 rounded-t-2xl bg-[#9481ce] text-white">
        <div>
          <h2 className="text-sm font-semibold tracking-wide">
            Module Field Settings
          </h2>
          <p className="text-[11px] text-violet-100">
            {module.replaceAll("_", " ")}
          </p>
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-full bg-white text-[#6b59c6] px-4 py-1.5 text-xs font-semibold shadow hover:bg-violet-50 disabled:opacity-60"
        >
          <FaSave className="text-[11px]" />
          {isSaving ? "Saving…" : "Save"}
        </motion.button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 h-[calc(100vh-250px)] md:h-[calc(100vh-170px)] overflow-y-auto">
        {Object.entries(grouped).map(([type, items]) => (
          <div key={type} className="border rounded-xl overflow-hidden">
            <div className="bg-slate-100 px-4 py-2 text-xs font-semibold uppercase text-slate-600">
              {type}
            </div>

            <div className="divide-y">
              {items.map((field) => {
                const index = fields.findIndex((f) => f.name === field.name);

                return (
                  <div
                    key={field.name}
                    className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition"
                  >
                    {/* Left */}
                    <div>
                      <div className="text-sm font-medium text-slate-800">
                        {field.label}
                      </div>
                      <div className="text-[11px] text-slate-500 flex items-center gap-2">
                        {field.name}
                        {field.mandatory && (
                          <span className="inline-flex items-center gap-1 text-rose-500">
                            <FaLock className="text-[9px]" />
                            mandatory
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right */}
                    <button
                      disabled={field.mandatory}
                      onClick={() => toggleVisibility(index)}
                      className={[
                        "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold transition",
                        field.visibility
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-200 text-slate-600",
                        field.mandatory
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:opacity-90",
                      ].join(" ")}
                    >
                      {field.visibility ? (
                        <>
                          <FaEye className="text-[11px]" />
                          Visible
                        </>
                      ) : (
                        <>
                          <FaEyeSlash className="text-[11px]" />
                          Hidden
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 border-t bg-slate-50 rounded-b-2xl text-[11px] text-slate-500">
        Mandatory fields cannot be hidden. Changes apply instantly to Add/Edit
        forms.
      </div>
    </div>
  );
};

export default ModuleFieldSettings;
